<template>
  <div class="card">
    <div class="card-body promotion">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h4 class="title mb-1">{{ $t("promotion.title") }}</h4>
          <div class="subtitle">{{ $t("promotion.subtitle") }}</div>
        </div>
        <a-button icon="arrow-left" @click="goBack">
          {{ $t("promotion.back") }}
        </a-button>
      </div>

      <div v-if="loading" class="text-center py-5">
        <a-spin size="large" />
      </div>

      <template v-else>
        <!-- Year transition -->
        <div class="year-flow" v-if="plan.sourceYear">
          <div class="year-chip source">
            <div class="chip-label">{{ $t("promotion.fromYear") }}</div>
            <div class="chip-value">{{ plan.sourceYear.name }}</div>
          </div>
          <a-icon type="arrow-right" class="flow-arrow" />
          <div class="year-chip target" v-if="plan.targetYear">
            <div class="chip-label">{{ $t("promotion.toYear") }}</div>
            <div class="chip-value">{{ plan.targetYear.name }}</div>
          </div>
          <div class="year-chip target missing" v-else>
            <div class="chip-value">{{ $t("promotion.noNextYearShort") }}</div>
          </div>
        </div>

        <!-- Blockers -->
        <a-alert
          v-if="hasNoNextYear"
          type="error"
          show-icon
          class="mt-3"
          :message="$t('promotion.noNextYearTitle')"
          :description="$t('promotion.noNextYearDesc')"
        />
        <a-alert
          v-else-if="missingLevels.length"
          type="warning"
          show-icon
          class="mt-3"
          :message="$t('promotion.missingTitle')"
        >
          <template slot="description">
            <div>{{ $t("promotion.missingDesc") }}</div>
            <ul class="mb-0 mt-2">
              <li v-for="lvl in missingLevels" :key="lvl">
                {{ levelLabel(lvl) }}
              </li>
            </ul>
          </template>
        </a-alert>

        <!-- Result (after execution) -->
        <a-result
          v-if="result"
          status="success"
          :title="$t('promotion.doneTitle')"
        >
          <template slot="subTitle">
            <div class="result-stats">
              <span class="rs promote">
                <b>{{ result.promoted }}</b> {{ $t("promotion.promoted") }}
              </span>
              <span class="rs keep">
                <b>{{ result.kept }}</b> {{ $t("promotion.kept") }}
              </span>
              <span class="rs archive">
                <b>{{ result.archived }}</b> {{ $t("promotion.archived") }}
              </span>
              <span class="rs skip" v-if="result.skipped">
                <b>{{ result.skipped }}</b> {{ $t("promotion.skipped") }}
              </span>
            </div>
          </template>
          <template slot="extra">
            <a-button type="primary" @click="goBack">
              {{ $t("promotion.back") }}
            </a-button>
          </template>
        </a-result>

        <!-- Plan -->
        <template v-else>
          <a-empty
            v-if="!plan.groups.length && !hasNoNextYear"
            class="mt-5"
            :description="$t('promotion.noStudents')"
          />

          <div v-else-if="!hasNoNextYear" class="mt-4">
            <!-- summary -->
            <div class="summary-bar">
              <div class="summary-item">
                <span class="dot promote"></span>
                {{ $t("promotion.toPromote") }}: <b>{{ totalPromote }}</b>
              </div>
              <div class="summary-item">
                <span class="dot keep"></span>
                {{ $t("promotion.toKeep") }}: <b>{{ totalKeep }}</b>
              </div>
              <div class="summary-item">
                <span class="dot archive"></span>
                {{ $t("promotion.toArchive") }}: <b>{{ totalArchive }}</b>
              </div>
            </div>

            <!-- promote groups -->
            <div
              v-for="g in promoteGroups"
              :key="'p' + g.sourceLevel"
              class="promo-row"
            >
              <div class="lvl-badge">{{ levelLabel(g.sourceLevel) }}</div>
              <a-icon type="arrow-right" class="row-arrow" />
              <div class="lvl-badge target">
                {{ levelLabel(g.targetLevel) }}
              </div>
              <div class="row-info">
                <a-icon type="team" />
                {{ g.count }} {{ $t("promotion.students") }}
                <span class="cls-name" v-if="g.targetClassroom">
                  → {{ g.targetClassroom.name }}
                </span>
              </div>
            </div>

            <!-- terminal groups -->
            <div
              v-for="g in terminalGroups"
              :key="'t' + g.sourceLevel"
              class="terminal-card"
            >
              <div class="terminal-head">
                <div>
                  <a-icon type="flag" class="mr-1" />
                  <b>{{ levelLabel(g.sourceLevel) }}</b>
                  <a-tag color="red" class="ml-2">
                    {{ $t("promotion.endOfCycle") }}
                  </a-tag>
                </div>
                <div class="terminal-counts">
                  <span class="keep">{{ keptCount(g) }} {{ $t("promotion.keptShort") }}</span>
                  <span class="archive">
                    {{ g.count - keptCount(g) }} {{ $t("promotion.archivedShort") }}
                  </span>
                </div>
              </div>

              <div class="terminal-hint">
                <a-icon type="info-circle" />
                {{
                  g.keepClassroom
                    ? $t("promotion.terminalHint")
                    : $t("promotion.terminalHintNoKeep")
                }}
              </div>

              <a-checkbox-group
                v-if="g.keepClassroom"
                v-model="keepers[g.sourceLevel]"
                class="student-grid"
              >
                <a-checkbox
                  v-for="s in g.students"
                  :key="s._id"
                  :value="s._id"
                  class="student-check"
                >
                  {{ s.fullName }}
                  <a-tag v-if="s.alreadyPlaced" size="small">
                    {{ $t("promotion.alreadyPlaced") }}
                  </a-tag>
                </a-checkbox>
              </a-checkbox-group>

              <div v-else class="student-plain">
                <span
                  v-for="s in g.students"
                  :key="s._id"
                  class="student-tag"
                >
                  {{ s.fullName }}
                </span>
              </div>
            </div>

            <!-- execute -->
            <div class="actions">
              <a-popconfirm
                :title="$t('promotion.confirmText')"
                :ok-text="$t('promotion.confirmOk')"
                :cancel-text="$t('action.annuler')"
                @confirm="execute"
                :disabled="!plan.ready || executing"
              >
                <a-button
                  type="primary"
                  size="large"
                  icon="rise"
                  :loading="executing"
                  :disabled="!plan.ready"
                >
                  {{ $t("promotion.execute") }}
                </a-button>
              </a-popconfirm>
              <span v-if="!plan.ready" class="not-ready">
                {{ $t("promotion.notReady") }}
              </span>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import router from "@/router";
import { mapState } from "vuex";
import { getAllLevels } from "@/config/LevelConstants";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "PromotionScolarite",
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding && this.activeBuilding.countryCode;
    },
    schoolType() {
      const t = this.activeBuilding && this.activeBuilding.type;
      return Array.isArray(t) ? t : t ? [t] : [];
    },
    allLevels() {
      return getAllLevels(this.$t.bind(this), this.countryCode);
    },
    hasNoNextYear() {
      return (this.plan.blockers || []).some((b) => b.code === "NO_NEXT_YEAR");
    },
    missingLevels() {
      return (this.plan.blockers || [])
        .filter((b) => b.code === "MISSING_AFFECTATION")
        .map((b) => b.level);
    },
    promoteGroups() {
      return this.plan.groups.filter((g) => !g.terminal);
    },
    terminalGroups() {
      return this.plan.groups.filter((g) => g.terminal);
    },
    totalPromote() {
      return this.promoteGroups.reduce((s, g) => s + g.count, 0);
    },
    totalKeep() {
      return this.terminalGroups.reduce((s, g) => s + this.keptCount(g), 0);
    },
    totalArchive() {
      return this.terminalGroups.reduce(
        (s, g) => s + (g.count - this.keptCount(g)),
        0
      );
    },
  },
  data() {
    return {
      loading: true,
      executing: false,
      plan: { sourceYear: null, targetYear: null, ready: false, blockers: [], groups: [] },
      keepers: {},
      result: null,
    };
  },
  created() {
    this.fetchPlan();
  },
  methods: {
    goBack() {
      router.push("/inscriptionScolarite");
    },
    levelLabel(level) {
      return this.allLevels[String(level)] || String(level);
    },
    keptCount(group) {
      const arr = this.keepers[group.sourceLevel];
      return arr ? arr.length : 0;
    },
    fetchPlan() {
      this.loading = true;
      apiClient
        .post("/promotion/plan", {
          schoolType: this.schoolType,
          countryCode: this.countryCode,
        })
        .then((res) => {
          this.plan = res.data;
          // Prepare reactive keeper buckets for each terminal level.
          (this.plan.groups || []).forEach((g) => {
            if (g.terminal) this.$set(this.keepers, g.sourceLevel, []);
          });
        })
        .catch((e) => showApiError(this, e))
        .finally(() => (this.loading = false));
    },
    execute() {
      this.executing = true;
      apiClient
        .post("/promotion/execute", {
          schoolType: this.schoolType,
          countryCode: this.countryCode,
          keepers: this.keepers,
        })
        .then((res) => {
          this.result = res.data;
          this.$message.success(this.$t("promotion.doneTitle"));
        })
        .catch((e) => showApiError(this, e))
        .finally(() => (this.executing = false));
    },
  },
};
</script>

<style scoped>
.promotion {
  padding: 4px;
}
.title {
  font-weight: 700;
  color: #1f2733;
}
.subtitle {
  color: #8a94a6;
  font-size: 13px;
}
.year-flow {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
}
.year-chip {
  padding: 10px 18px;
  border-radius: 12px;
  background: #f5f7fa;
  min-width: 150px;
  text-align: center;
}
.year-chip.target {
  background: #e6f7ff;
}
.year-chip.target.missing {
  background: #fff1f0;
  color: #cf1322;
}
.chip-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #8a94a6;
  letter-spacing: 0.03em;
}
.chip-value {
  font-size: 18px;
  font-weight: 700;
  color: #1f2733;
}
.flow-arrow {
  font-size: 22px;
  color: #bfbfbf;
}
.summary-bar {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: #fafbfc;
  border-radius: 10px;
  margin-bottom: 18px;
}
.summary-item {
  font-size: 14px;
  color: #4a5568;
}
.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}
.dot.promote {
  background: #1890ff;
}
.dot.keep {
  background: #faad14;
}
.dot.archive {
  background: #ff4d4f;
}
.promo-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  margin-bottom: 10px;
}
.lvl-badge {
  padding: 4px 12px;
  border-radius: 8px;
  background: #f0f2f5;
  font-weight: 600;
  color: #4a5568;
  min-width: 90px;
  text-align: center;
}
.lvl-badge.target {
  background: #e6f7ff;
  color: #096dd9;
}
.row-arrow {
  color: #bfbfbf;
}
.row-info {
  margin-left: auto;
  color: #4a5568;
  font-size: 13px;
}
.cls-name {
  color: #096dd9;
  font-weight: 500;
}
.terminal-card {
  border: 1px solid #ffccc7;
  background: #fff7f6;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
}
.terminal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.terminal-counts {
  font-size: 13px;
}
.terminal-counts .keep {
  color: #d48806;
  margin-right: 12px;
}
.terminal-counts .archive {
  color: #cf1322;
}
.terminal-hint {
  font-size: 13px;
  color: #8a6d3b;
  margin-bottom: 12px;
}
.student-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  width: 100%;
}
.student-check {
  margin-left: 0;
}
.student-plain {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.student-tag {
  background: #fff;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 13px;
  color: #4a5568;
}
.actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 20px;
}
.not-ready {
  color: #cf1322;
  font-size: 13px;
}
.result-stats {
  display: flex;
  gap: 18px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 8px;
}
.rs {
  font-size: 14px;
  color: #4a5568;
}
.rs.promote b {
  color: #1890ff;
}
.rs.keep b {
  color: #faad14;
}
.rs.archive b {
  color: #ff4d4f;
}
</style>
